/**
 * GiftPurchasePage — `/gift/buy`.
 *
 * Public route (auth optional; `purchaser_id` on the backend is nullable).
 * Renders a two-way toggle between:
 *
 *   • **Credit gift** — dollar amount (converted to `amount_cents`).
 *     On success the recipient's ledger receives a `source="gift"` row
 *     equal to `amount_cents/100` and the recipient's `credit_balance`
 *     is $inc'd via the shared `_write_ledger_entry_and_inc_balance`
 *     helper (same helper `admin_grant` uses — single write path).
 *
 *   • **Service gift** — free-form offering (e.g. "One free monthly
 *     plucking"). On redeem, the gift is marked consumed; applying
 *     the offering to a booking is Stripe-phase work (grep marker
 *     `TODO (Stripe phase): apply gifted service to booking/payout`
 *     in `backend/gifts.py`).
 *
 * Flow: purchase → auto-invoke `/mock-pay` (v1 stubs the real Stripe
 * PaymentIntent — grep marker `TODO (Stripe phase): charge gifter`)
 * → display the 12-char base32 redemption code with a copy affordance
 * + a link the recipient can visit at `/gift/redeem`.
 *
 * Copy tone (per dispatch): brand-friendly, encouragement framing. No
 * shame copy. Redemption is deferred delivery — the app does NOT
 * currently email anything (grep marker
 * `TODO: delivery method — code-entry vs email auto-apply (undecided)`
 * in `backend/gifts.py`). The purchaser is expected to hand-off the
 * code themselves in v1.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Gift, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";

const GIFT_TYPES = [
  { value: "credit", label: "PooBucks credit" },
  { value: "service", label: "Free plucking service" },
];

export default function GiftPurchasePage() {
  const { creditLabel } = useConfig();
  const [giftType, setGiftType] = useState("credit");
  const [amountDollars, setAmountDollars] = useState("25");
  const [offeringId, setOfferingId] = useState("svc-monthly");
  const [offeringLabel, setOfferingLabel] = useState("One free monthly plucking");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [copied, setCopied] = useState(false);

  function reset() {
    setReceipt(null);
    setCopied(false);
  }

  async function handlePurchase(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const body = { gift_type: giftType };
      if (giftType === "credit") {
        const dollars = Number(amountDollars);
        if (!Number.isFinite(dollars) || dollars <= 0) {
          throw new Error("Enter a positive amount to gift.");
        }
        body.amount_cents = Math.round(dollars * 100);
      } else {
        body.offering_id = offeringId.trim() || null;
        body.offering_label = offeringLabel.trim();
        if (!body.offering_label) {
          throw new Error("Give the service gift a name (e.g. \"One free plucking\").");
        }
      }
      const email = recipientEmail.trim();
      if (email) body.recipient_email = email;
      // Step 1: create the gift record (status="created", no code yet).
      const { data: created } = await api.post("/gifts", body);
      // Step 2: mock-pay to mint the redemption code (v1 stubs the real
      // Stripe charge — grep the two `TODO (Stripe phase)` markers in
      // `backend/gifts.py` to see the eventual insertion points).
      const { data: paid } = await api.post(`/gifts/${created.id}/mock-pay`);
      setReceipt(paid);
      toast.success("Gift ready to share!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail) || err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!receipt?.redemption_code) return;
    try {
      await navigator.clipboard.writeText(receipt.redemption_code);
      setCopied(true);
      toast.success("Code copied to clipboard.");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select the code and copy manually.");
    }
  }

  if (receipt) {
    return (
      <div className="min-h-screen bg-pp-surface2" data-testid="gift-purchase-page-receipt">
        <div className="mx-auto max-w-2xl px-6 py-16">
          <Card className="border-brand-orange/20 bg-pp-surface p-10 text-center">
            <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-orange/10">
              <Gift className="h-8 w-8 text-brand-orange" aria-hidden="true" />
            </div>
            <h1
              className="font-heading text-3xl font-bold text-pp-text sm:text-4xl"
              data-testid="gift-receipt-heading"
            >
              Your gift is ready to share!
            </h1>
            <p className="mt-3 text-base text-pp-text-muted">
              {receipt.gift_type === "credit"
                ? `You've gifted $${(receipt.amount_cents / 100).toFixed(2)} in ${creditLabel}.`
                : `You've gifted a service: "${receipt.offering_label}".`}
            </p>
            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-[0.10em] text-pp-text-muted">
                Redemption code
              </p>
              <div
                className="mt-3 inline-flex items-center gap-3 rounded-lg border-2 border-dashed border-brand-orange bg-pp-surface2 px-6 py-4"
                data-testid="gift-receipt-code-wrapper"
              >
                <span
                  className="font-heading text-3xl font-bold tracking-[0.15em] text-pp-text tabular-nums"
                  data-testid="gift-receipt-code"
                >
                  {receipt.redemption_code}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  data-testid="gift-receipt-copy-btn"
                  aria-label="Copy code"
                >
                  {copied ? (
                    <Check className="h-5 w-5 text-brand-orange" aria-hidden="true" />
                  ) : (
                    <Copy className="h-5 w-5" aria-hidden="true" />
                  )}
                </Button>
              </div>
            </div>
            <p className="mx-auto mt-6 max-w-md text-sm text-pp-text-muted">
              Share this code with the lucky recipient. They can redeem it any time in
              the next 12 months at{" "}
              <Link
                to="/gift/redeem"
                className="font-semibold text-brand-orange underline-offset-2 hover:underline"
                data-testid="gift-receipt-redeem-link"
              >
                pooplucker.com/gift/redeem
              </Link>
              .
            </p>
            <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button
                type="button"
                onClick={reset}
                variant="outline"
                data-testid="gift-receipt-new-btn"
              >
                Gift another
              </Button>
              <Button asChild data-testid="gift-receipt-home-btn">
                <Link to="/">Back to home</Link>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pp-surface2" data-testid="gift-purchase-page">
      <FloatingThemeToggle />
      <div className="mx-auto max-w-2xl px-6 py-16">
        <div className="mb-10 text-left">
          <div className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-brand-orange/10">
            <Gift className="h-7 w-7 text-brand-orange" aria-hidden="true" />
          </div>
          <h1
            className="font-heading text-4xl font-bold text-pp-text sm:text-5xl"
            data-testid="gift-purchase-heading"
          >
            Gift a PooPluck.
          </h1>
          <p className="mt-3 text-lg text-pp-text-muted">
            Show a dog-loving friend some love — send them {creditLabel} they can
            spend on plucks, or gift them a specific service outright.
          </p>
        </div>

        <Card className="border-pp-border bg-pp-surface p-8">
          <form onSubmit={handlePurchase} className="space-y-6" data-testid="gift-purchase-form">
            <div>
              <Label className="mb-2 block text-sm font-semibold text-pp-text">
                Gift type
              </Label>
              <div
                className="grid grid-cols-2 gap-3"
                role="radiogroup"
                aria-label="Gift type"
                data-testid="gift-type-toggle"
              >
                {GIFT_TYPES.map((opt) => {
                  const active = giftType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setGiftType(opt.value)}
                      className={
                        "rounded-lg border-2 px-4 py-3 text-left transition-colors " +
                        (active
                          ? "border-brand-orange bg-brand-orange/5 text-pp-text"
                          : "border-pp-border bg-pp-surface text-pp-text-muted hover:border-brand-orange/40")
                      }
                      data-testid={`gift-type-option-${opt.value}`}
                    >
                      <span className="block text-sm font-semibold">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {giftType === "credit" && (
              <div data-testid="gift-credit-fields">
                <Label htmlFor="gift-amount" className="mb-2 block text-sm font-semibold text-pp-text">
                  Amount (USD)
                </Label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-pp-text-muted">
                    $
                  </span>
                  <Input
                    id="gift-amount"
                    type="number"
                    min="1"
                    step="1"
                    value={amountDollars}
                    onChange={(e) => setAmountDollars(e.target.value)}
                    className="pl-8"
                    data-testid="gift-amount-input"
                  />
                </div>
              </div>
            )}

            {giftType === "service" && (
              <div className="space-y-4" data-testid="gift-service-fields">
                <div>
                  <Label
                    htmlFor="gift-offering-label"
                    className="mb-2 block text-sm font-semibold text-pp-text"
                  >
                    Service description
                  </Label>
                  <Input
                    id="gift-offering-label"
                    value={offeringLabel}
                    onChange={(e) => setOfferingLabel(e.target.value)}
                    placeholder="One free monthly plucking"
                    maxLength={200}
                    data-testid="gift-offering-label-input"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="gift-offering-id"
                    className="mb-2 block text-sm font-semibold text-pp-text"
                  >
                    Offering code (optional)
                  </Label>
                  <Input
                    id="gift-offering-id"
                    value={offeringId}
                    onChange={(e) => setOfferingId(e.target.value)}
                    placeholder="svc-monthly"
                    data-testid="gift-offering-id-input"
                  />
                </div>
              </div>
            )}

            <div>
              <Label
                htmlFor="gift-recipient-email"
                className="mb-2 block text-sm font-semibold text-pp-text"
              >
                Recipient email (optional)
              </Label>
              <Input
                id="gift-recipient-email"
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="friend@example.com"
                data-testid="gift-recipient-email-input"
              />
              <p className="mt-2 text-xs text-pp-text-muted">
                We store it with the gift but nothing is emailed yet — hand-off is
                on you for now.
              </p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-orange text-white hover:bg-brand-orangeDark"
              data-testid="gift-purchase-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  Wrapping your gift…
                </>
              ) : (
                "Send the gift"
              )}
            </Button>

            <p className="text-center text-xs text-pp-text-muted">
              Have a code someone shared with you?{" "}
              <Link
                to="/gift/redeem"
                className="font-semibold text-brand-orange underline-offset-2 hover:underline"
                data-testid="gift-purchase-goto-redeem"
              >
                Redeem it here
              </Link>
              .
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
}
